import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import PageHeader from '@uikit/old/PageHeader/PageHeader';
import HeaderActions from '@uikit/old/HeaderTemplate/HeaderActions/HeaderActions';

import { FilterFieldType } from '@ts/filterField.type';
import OrdersTable from '@components/Publisher/Orders/OrdersTable/OrdersTable';
import {
  PublisherOrderFilterField,
  PublisherOrderFilterFieldMap,
} from 'respona_api/generated/order-publisher_pb';

import { FilterJoins, FilterOperationsMapForString } from '@constants/filters/filterOperations';

import {
  getPublisherOrderFilterFields,
  getPublisherOrderFilterOptionsMap,
} from '@constants/filters/publisherOrderFilterFields';
import { parseQueryParamFilters } from '@utils/url.utils';
import debounce from '@utils/debounce';
import useNonInitialEffect from '@hooks/useNonInitialEffect';

import './OrdersPage.css';

type OrdersFilterType = FilterFieldType<PublisherOrderFilterFieldMap>;

const SEARCH_DEBOUNCE_MS = 500;

/**
 * Default row shown in the filters tooltip when nothing is applied yet.
 */
const initialFilterState: Record<string, OrdersFilterType> = {
  '1': {
    order: 0,
    field: PublisherOrderFilterField.STRING_ORDER_DOMAIN,
    value: '',
    operation: FilterOperationsMapForString[2].value,
    join: FilterJoins[0].value,
  },
};

/**
 * The header search box matches either the website or the order number, so a
 * publisher can paste an order number straight out of a notification email.
 */
const SEARCHABLE_FIELDS = [
  PublisherOrderFilterField.STRING_ORDER_DOMAIN,
  PublisherOrderFilterField.STRING_ORDER_NUMBER,
];

const buildSearchFilters = (searchString: string): OrdersFilterType[] => {
  const trimmed = searchString.trim();

  if (trimmed.length === 0) {
    return [];
  }

  return SEARCHABLE_FIELDS.map((field, index) => ({
    order: index,
    field,
    value: trimmed,
    operation: FilterOperationsMapForString[2].value,
    join: FilterJoins[1].value,
  }));
};

function OrdersPage(): JSX.Element {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const queryParamFilters = params.get('filters');

  // Notification links deep-link into the page with a pre-built filter set,
  // e.g. ?filters=[{"type":"STRING_ORDER_NUMBER","value":"100021"}]
  const [appliedFilters, setAppliedFilters] = useState<OrdersFilterType[]>(
    () =>
      // Numeric-looking values come back as numbers, which the api layer stringifies.
      parseQueryParamFilters({
        queryParamFilters,
        filterFieldMap: PublisherOrderFilterField,
        getOptionsMap: (field) => getPublisherOrderFilterOptionsMap()[field],
        currentLength: 0,
      }) as OrdersFilterType[]
  );
  // Search lives in its own state so typing never overwrites a filter that came
  // from the URL or from the filters tooltip.
  const [searchFilters, setSearchFilters] = useState<OrdersFilterType[]>([]);

  // Navigating to another deep link while already on the page keeps the same
  // mounted component, so the URL has to be re-read rather than only parsed once.
  useNonInitialEffect(() => {
    setAppliedFilters(
      parseQueryParamFilters({
        queryParamFilters,
        filterFieldMap: PublisherOrderFilterField,
        getOptionsMap: (field) => getPublisherOrderFilterOptionsMap()[field],
        currentLength: 0,
      }) as OrdersFilterType[]
    );
  }, [queryParamFilters]);

  const debouncedSetSearchString = useCallback(
    debounce((value: string) => setSearchFilters(buildSearchFilters(value)), SEARCH_DEBOUNCE_MS),
    []
  );

  // Keeps the filters tooltip in sync with what is actually applied — without
  // this, deep-linked filters are invisible and the first "Apply" wipes them.
  const controlledFilters = useMemo<Record<string, OrdersFilterType>>(() => {
    if (appliedFilters.length === 0) {
      return initialFilterState;
    }

    return appliedFilters.reduce(
      (acc, filter, index) => ({ ...acc, [String(index + 1)]: filter }),
      {}
    );
  }, [appliedFilters]);

  const tableFilters = useMemo(
    () => [...searchFilters, ...appliedFilters],
    [searchFilters, appliedFilters]
  );

  return (
    <div className="page-table" data-testid="publisher-orders-page">
      <div className="page-table__content">
        <PageHeader
          title="Orders"
          isFixed
          howToLink="https://help.respona.com/en/articles/6332415-how-to-use-campaigns"
        >
          <HeaderActions
            testIdPrefix="publisher-orders"
            handleSearchValue={debouncedSetSearchString}
            searchPlaceholder="Search by website or order number"
            initialFilterState={initialFilterState}
            filterFields={getPublisherOrderFilterFields()}
            appliedFilters={appliedFilters}
            onApplyFilters={(filters) => setAppliedFilters(filters)}
            controlledFilters={controlledFilters}
            skipDayCheck
          />
        </PageHeader>
        <div>
          <OrdersTable appliedFilters={tableFilters} />
        </div>
      </div>
    </div>
  );
}

export default OrdersPage;
